import { useCallback, useEffect, useRef, useState } from 'react';

import { AskSynclyAnswerType } from '@/entities/ask-syncly/ask-syncly-constants';
import {
  requestDeepResearch,
  type DeepResearchSSEEvent,
} from '@/entities/ask-syncly/sse/request-deep-research';
import type { CurrentAnswerBlock } from '@/entities/ask-syncly/ask-syncly-types';
import type { DateRange } from '@/entities/query/query-types';
import type { SSEController } from '@/shared/api-client/sse-client';
import { randomBetween } from '@/shared/utils/random-between';
import { captureException } from '@sentry/react';
import { toast } from 'sonner';
import { usePublicFilter } from '../../shared/public-filter/use-public-filter';

export const useDeepResearch = (params: {
  queryId: string | null;
  dateRange: DateRange | null;
  onDone?: () => void;
  sessionId?: string;
}) => {
  const sseController = useRef<SSEController>(null);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<Partial<CurrentAnswerBlock> | null>(null);
  const { queryId, dateRange, onDone, sessionId } = params;
  const { publicFilter } = usePublicFilter(queryId);

  const submit = useCallback(
    (question: string) => {
      if (!queryId || !sessionId) return;
      setData({
        id: `block-question-${randomBetween(1, 100000)}`,
        question: question,
        asked_at: new Date(),
        replied_at: new Date(),
      });
      setLoading(true);
      const controller = requestDeepResearch({
        session_id: sessionId,
        question,
        dateRange,
        publicFilter,
        options: {
          onMessage: (event) => {
            setData((prev) => messageHandler(prev, event));
          },
          onError: (error) => {
            console.error(error);
            captureException('Failed to submit chat');
            toast('Failed to submit');
            setLoading(false);
            setData(null);
          },
          onDone: () => {
            setLoading(false);
            onDone?.();
          },
        },
      });

      sseController.current = controller;

      return sseController.current;
    },
    [queryId, sessionId, dateRange, publicFilter, onDone],
  );

  const clear = useCallback(() => {
    setData(null);
  }, []);

  useEffect(() => {
    return () => {
      sseController.current?.abort();
      clear();
      setLoading(false);
    };
  }, [clear, sessionId]);

  return { data, submit, clear, loading };
};

const messageHandler = (
  prev: Partial<CurrentAnswerBlock> | null,
  event: DeepResearchSSEEvent,
): Partial<CurrentAnswerBlock> | null => {
  const type = event.data.type;
  switch (type) {
    case 'transit': {
      const status = event.data.content;
      return {
        ...(prev ?? {}),
        status,
      };
    }
    case 'reason': {
      const reasons = event.data.content;
      return {
        ...(prev ?? {}),
        reasons: [...(prev?.reasons ?? []), reasons],
      };
    }
    case 'answer': {
      const type = event.data.content.type;
      switch (type) {
        case 'topic_analysis': {
          const topicAnalysis = event.data.content.content;
          return {
            ...(prev ?? {}),
            answers: [
              ...(prev?.answers ?? []),
              { type: AskSynclyAnswerType.TOPIC_ANALYSIS, content: topicAnalysis },
            ],
          };
        }
        case 'statistical_analysis': {
          const statisticalAnalysis = event.data.content.content;
          return {
            ...(prev ?? {}),
            answers: [
              ...(prev?.answers ?? []),
              {
                type: AskSynclyAnswerType.STATISTICAL_ANALYSIS,
                content: statisticalAnalysis,
              },
            ],
          };
        }
        case 'plain_text': {
          const plainText = event.data.content.content;
          return {
            ...(prev ?? {}),
            answers: [
              ...(prev?.answers ?? []),
              { type: AskSynclyAnswerType.PLAIN_TEXT, content: plainText },
            ],
          };
        }
        default:
          console.error(`Unknown answer type: ${type}`);
          return prev;
      }
    }
    default:
      console.error(`Unknown event type: ${type}`);
      return prev;
  }
};
